import React, { useEffect, useState } from "react";
import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import dashboardAPI from "services/dashboard";

const columnHelper = createColumnHelper();

const ComplexTable = () => {
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewFromDate, setPreviewFromDate] = useState("");
  const [previewToDate, setPreviewToDate] = useState("");

  // ── Today's date string for max attribute ──
  const today = new Date().toISOString().split("T")[0];

  // ── Date Filter State ──
  const [dateMode, setDateMode] = useState("single");
  const [selectedDate, setSelectedDate] = useState(today);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const columns = [
    columnHelper.display({
      id: "sno",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">S.NO</p>
      ),
      cell: (info) => (
        <p className="text-xs font-bold text-navy-700 dark:text-white">
          {info.row.index + 1}
        </p>
      ),
    }),
    columnHelper.accessor("date", {
      id: "date",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">DATE</p>
      ),
      cell: (info) => (
        <p className="text-xs font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("emp_code", {
      id: "emp_code",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">EMP</p>
      ),
      cell: (info) => (
        <p className="text-xs font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">NAME</p>
      ),
      cell: (info) => (
        <p className="max-w-[80px] truncate text-xs font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("department", {
      id: "department",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">DEPT</p>
      ),
      cell: (info) => (
        <p className="max-w-[80px] truncate text-xs font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">ROLE</p>
      ),
      cell: (info) => (
        <p className="max-w-[70px] truncate text-xs font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("punch_in_time", {
      id: "punch_in_time",
      header: () => (
        <p className="text-xs font-bold text-gray-600 dark:text-white">
          PUNCH IN
        </p>
      ),
      cell: (info) => (
        <p className="text-xs font-bold text-red-500 dark:text-red-400">
          {info.getValue()}
        </p>
      ),
    }),
  ];

  const fetchData = () => {
    setLoading(true);
    setError(null);

    const params = {};
    if (dateMode === "single") {
      if (selectedDate) params.from_date = selectedDate;
    } else {
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
    }

    dashboardAPI.getLatePunchIn(
      params,
      (response) => {
        setData(response?.employees || []);
        setPreviewFromDate(response?.from_date || "");
        setPreviewToDate(response?.to_date || "");
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to fetch late punch-in data");
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [dateMode, selectedDate, fromDate, toDate]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card extra={"w-full h-full px-4 pb-4 sm:overflow-x-auto"}>
      {/* ── Header ── */}
      <div className="relative flex items-center justify-between pb-2 pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Late Punch-In
        </div>
        <CardMenu />
      </div>

      {/* ── Date Filters ── */}
      <div className="mb-3 flex flex-col gap-6">
        {/* Today / Date Range Toggle */}
        <div className="flex items-center gap-1 self-start rounded-lg border-2 border-gray-200 bg-white dark:border-gray-700 dark:bg-navy-700">
          <button
            onClick={() => setDateMode("single")}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              dateMode === "single"
                ? "bg-blue-500 text-white"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setDateMode("range")}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              dateMode === "range"
                ? "bg-blue-500 text-white"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
            }`}
          >
            Date Range
          </button>
        </div>

        {/* Single Date — max set to today */}
        {dateMode === "single" && (
          <div className="flex items-center gap-2">
            <label className="shrink-0 text-xs font-semibold text-gray-600 dark:text-gray-300">
              Date:
            </label>
            <input
              type="date"
              value={selectedDate}
              max={today}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="rounded-lg border-2 border-gray-200 bg-white px-2 py-1.5 text-xs text-navy-700 transition focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-navy-700 dark:text-white"
            />
          </div>
        )}

        {/* From & To Date — both max set to today, fully independent */}
        {dateMode === "range" && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="shrink-0 text-xs font-semibold text-gray-600 dark:text-gray-300">
                From   :
              </label>
              <input
                type="date"
                value={fromDate}
                max={today}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border-2 border-gray-200 bg-white px-2 py-1.5 text-xs text-navy-700 transition focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="shrink-0 text-xs font-semibold text-gray-600 dark:text-gray-300">
                To  :
              </label>
              <input
                type="date"
                value={toDate}
                max={today}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border-2 border-gray-200 bg-white px-2 py-1.5 text-xs text-navy-700 transition focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Scrollable Table ── */}
      <div className="overflow-x-auto">
        <div className="max-h-[240px] overflow-y-auto">
          {loading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              Loading late punch-in data...
            </div>
          ) : error || data.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              No late punch-in records found.
            </div>
          ) : (
            <table className="w-full min-w-[400px]">
              <thead className="sticky top-0 z-10 bg-white dark:bg-navy-800">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-3 pt-2 text-start"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-100 dark:border-gray-700 ${
                      index % 2 === 0
                        ? "bg-gray-50 dark:bg-navy-900/20"
                        : "dark:bg-transparent bg-dark"
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="py-2 pr-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Record Count ── */}
      {!loading && data.length > 0 && (
        <div className="mt-2 text-right text-xs text-gray-400">
          {data.length} record{data.length !== 1 ? "s" : ""} found
        </div>
      )}
    </Card>
  );
};

export default ComplexTable;
